// todo: remove lodash on client
import flatten from 'lodash/flatten';
// todo: remove lodash on client
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import Container from '@glass/web/components/Layout/Container';
import Grid from '@glass/web/components/base/Unstable_Grid2';
import { LG, MD, XL } from '@glass/web/modules/theme/breakpointKeys';

import Breadcrumbs from '@glass/shared/components/Breadcrumbs/Breadcrumbs';
import EmphasisTitle from '@glass/shared/components/Content/EmphasisTitle';
import ResourceCard from '@glass/shared/components/Content/ResourceCard';
import ResourceLayoutPage from '@glass/shared/components/Content/ResourceLayoutPage';
import TagList from '@glass/shared/components/Content/TagList';
import blogPostPropTypes from '@glass/shared/components/Content/propTypes/blogPostPropTypes';
import BreadcrumbStructuredData from '@glass/shared/components/Head/BreadcrumbStructuredData';
import calcImageSizesV2 from '@glass/shared/components/Image/calcImageSizesV2';
import Link from '@glass/shared/components/Link/Link';
import {
  DISABLE_LOADING,
  LAZY_LOADING,
  SSR_LOADING,
} from '@glass/shared/components/Section/loadingMethods';
import { pushResourcesInterest } from '@glass/shared/modules/interests/pushInterestAction';

const itemProps = {
  [XL]: {
    xs: 12,
  },
  [LG]: {
    xs: 12,
    sm: 6,
  },
  [MD]: {
    xs: 12,
    sm: 6,
    md: 4,
  },
};

const calcSize = ({ idx }) => {
  let size = MD;
  if (idx >= 0 && idx < 4) {
    size = LG;
  }
  return size;
};

function BlogPost({ idx, blogPost }) {
  const size = useMemo(() => calcSize({ idx }), [idx]);
  const imageSizes = useMemo(() => calcImageSizesV2({ breakpoints: itemProps[size] }), [size]);
  return (
    <Grid {...itemProps[size]}>
      <ResourceCard blogPost={blogPost} imageSizes={imageSizes} size={size} />
    </Grid>
  );
}

BlogPost.propTypes = {
  blogPost: blogPostPropTypes,
  idx: PropTypes.number,
};

const DEFAULT_SPACING = 2;

function ResourceListPage({
  title,
  titleEmphasisIndex,
  precedingBreadcrumbItems,
  blogPosts = [],
  pageTitle,
  pageColor,
  description,
  resourceSectionLoadingMethod,
  slug,
  canonicalPath,
}) {
  const tags = useMemo(() => {
    if (!blogPosts) {
      return [];
    }
    return uniqBy(
      flatten(blogPosts.map(({ tagsCollection }) => tagsCollection?.items)),
      'slug',
    ).filter(({ slug: tagSlug }) => tagSlug !== slug);
  }, [blogPosts, slug]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (slug) {
      dispatch(pushResourcesInterest(slug));
    }
  }, [dispatch, slug]);

  return (
    <ResourceLayoutPage
      canonicalPath={canonicalPath}
      color={pageColor}
      description={description}
      pageTitle={pageTitle || title}
      resourceSectionLoadingMethod={resourceSectionLoadingMethod}
    >
      <Container>
        <Grid container spacing={DEFAULT_SPACING}>
          <Grid xs={12}>
            <BreadcrumbStructuredData items={precedingBreadcrumbItems} title={pageTitle || title} />
            <Breadcrumbs
              disableStructuredData
              isPageTitle
              trailingSeparator
              items={precedingBreadcrumbItems}
              linkComponent={Link}
              variant="h5"
            />
            <EmphasisTitle
              color={pageColor}
              componentColor="textPrimary"
              emphasisIndex={titleEmphasisIndex}
              title={title || pageTitle}
            />
          </Grid>
          <Grid xs={12}>
            <TagList disablePadding tags={tags} />
          </Grid>
          <Grid xs={12}>
            <div>
              <Grid container spacing={1}>
                {blogPosts.map((blogPost, idx) => (
                  <BlogPost key={blogPost.slug} blogPost={blogPost} idx={idx} />
                ))}
              </Grid>
            </div>
          </Grid>
        </Grid>
      </Container>
    </ResourceLayoutPage>
  );
}

ResourceListPage.propTypes = {
  blogPosts: PropTypes.arrayOf(blogPostPropTypes),
  canonicalPath: PropTypes.string,
  description: PropTypes.string,
  pageColor: PropTypes.string,
  pageTitle: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  precedingBreadcrumbItems: PropTypes.array,
  resourceSectionLoadingMethod: PropTypes.oneOf([DISABLE_LOADING, LAZY_LOADING, SSR_LOADING]),
  slug: PropTypes.string,
  title: PropTypes.string,
  titleEmphasisIndex: PropTypes.number,
};

export default ResourceListPage;
